import {useEffect} from "react";
import {Artifact, DirectSearchResult} from "../../../../types";
import {bffApi} from "../../bffApi";
import {metricUuid, newMetricEvent} from "../../metrics";
import {SearchResultsRenderer} from "./SearchResultsRenderer";
import {isValidFileType} from "../../../content/contentUtils";
import {ResourceType} from "../../../content/types";

type InitialSearchPayload = {
    searchId?: string;
    resultSetId?: string;
    results?: DirectSearchResult[];
};

// Rendering may be repeated by React or by state updates. A result set is an
// impression once, while every explicit result activation remains its own action.
const shownResultSets = new Set<string>();
const MAX_TRACKED_RESULT_SETS = 1_000;

const markResultSetShown = (resultSetId: string) => {
    if (shownResultSets.has(resultSetId)) return false;
    if (shownResultSets.size >= MAX_TRACKED_RESULT_SETS) {
        const oldest = shownResultSets.values().next().value;
        if (oldest) shownResultSets.delete(oldest);
    }
    shownResultSets.add(resultSetId);
    return true;
};

const resultType = (result: DirectSearchResult) => {
    if (
        isValidFileType(result.metadata?.resourceTypeid as ResourceType)
    )
        return "file";
    if (metricUuid(result.metadata?.topicId)) return "topic";
    return "external";
};

export function InitialSearchResults({
    artifact,
    messageContent,
}: {
    artifact: Artifact;
    messageContent?: string;
}) {
    const payload = artifact.payload as InitialSearchPayload;
    const searchId = metricUuid(payload.searchId);
    const resultSetId = metricUuid(payload.resultSetId);
    const results = payload.results ?? [];
    // The splash result artifact has this temporary browser-only identity. A
    // rehydrated thread seed must not become a fresh result-set impression.
    const isLiveInitialSearch = artifact.id.startsWith("search-");
    const [trackInteraction] = bffApi.endpoints.trackInteraction.useMutation();

    useEffect(() => {
        if (
            !isLiveInitialSearch ||
            !searchId ||
            !resultSetId ||
            !markResultSetShown(resultSetId)
        )
            return;
        void trackInteraction({
            ...newMetricEvent(),
            action: "search_results_shown",
            searchId,
            resultSetId,
            resultCount: results.length,
        });
    }, [isLiveInitialSearch, searchId, resultSetId, results.length, trackInteraction]);

    return (
        <SearchResultsRenderer
            artifact={artifact}
            messageContent={messageContent}
            onResultClick={(result, index) => {
                const subjectId = metricUuid(result.metadata?.topicId);
                if (!searchId || !resultSetId) return;
                void trackInteraction({
                    ...newMetricEvent(),
                    action: "search_result_opened",
                    searchId,
                    resultSetId,
                    subjectId,
                    position: index + 1,
                    resultType: resultType(result),
                });
            }}
        />
    );
}
